export const truncate = (str: string, num: number, ellipsis: boolean): string => {
  if (!str) return '';
  return str.length > num ? `${str.substring(0, num)}${ellipsis ? '…' : ''}` : str;
};

/**
 * @param str - floating point number as string type
 * @param num - precision decimals
 * @returns (str) formatted to (num) decimals
 */
export const truncateDecimals = (str: string, num: number) => {
  if (!str) return '';
  const [int, decimals] = str.split('.');
  return decimals ? `${int}.${decimals.substring(0, num)}` : int;
};

export const objectToQueryString = (query: object) =>
  Object.entries(query as Record<string, unknown>)
    .filter(([, val]) => typeof val !== 'undefined')
    .reduce((str, [key, val], i) => `${str}${i === 0 ? '' : '&'}${key}=${val}`, '?');

// Some environments have issues with RegEx that contain back-tracking, so we cannot
// use them.
export function commify(value: string | number): string {
  // Expand scientific notation (e.g., 2.999e+21) to a plain decimal string
  // preserving all digits, since the regex-based parser doesn't handle 'e' notation.
  let str = String(value);
  const sciMatch = str.match(/^(-?)(\d+)\.?(\d*)[eE]([+-]?\d+)$/);
  if (sciMatch) {
    const [, sign, intPart, fracPart, expStr] = sciMatch;
    const exp = parseInt(expStr, 10);
    const digits = intPart + fracPart;
    const dotPos = intPart.length + exp;

    if (dotPos >= digits.length) {
      str = sign + digits + '0'.repeat(dotPos - digits.length);
    } else if (dotPos <= 0) {
      str = sign + '0.' + '0'.repeat(-dotPos) + digits;
    } else {
      str = sign + digits.slice(0, dotPos) + '.' + digits.slice(dotPos);
    }
  }

  const comps = str.split('.');

  if (
    comps.length > 2 ||
    !comps[0].match(/^-?[0-9]*$/) ||
    (comps[1] && !comps[1].match(/^[0-9]*$/)) ||
    value === '.' ||
    value === '-.'
  ) {
    throw new Error(`invalid value: "value", ${value}`);
  }

  // Make sure we have at least one whole digit (0 if none)
  let whole = comps[0];

  let negative = '';
  if (whole.substring(0, 1) === '-') {
    negative = '-';
    whole = whole.substring(1);
  }

  // Make sure we have at least 1 whole digit with no leading zeros
  while (whole.substring(0, 1) === '0') {
    whole = whole.substring(1);
  }
  if (whole === '') {
    whole = '0';
  }

  let suffix = '';
  if (comps.length === 2) {
    suffix = '.' + (comps[1] || '0');
  }
  while (suffix.length > 2 && suffix[suffix.length - 1] === '0') {
    suffix = suffix.substring(0, suffix.length - 1);
  }

  const formatted = [];
  while (whole.length) {
    if (whole.length <= 3) {
      formatted.unshift(whole);
      break;
    } else {
      const index = whole.length - 3;
      formatted.unshift(whole.substring(index));
      whole = whole.substring(0, index);
    }
  }

  return negative + formatted.join(',') + suffix;
}
